import { useRouter } from "next/router";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from "react";

import { SmartphonesProps } from "app/smartphone/page";
import { SwapSmartphonesProps } from "app/swap/smartphone/page";
import { useI18n } from "utils/with-i18n.utils";

export type SelectedTagState = {
  selectedTagState: [
    string | undefined,
    Dispatch<SetStateAction<string | undefined>>,
  ];
};

export type CatalogFilters = {
  brand: string[];
  stock: string[];
  price: string[];
  size: string[];
  storage: string[];
};
export type CatalogFiltersAttribute = keyof CatalogFilters;

export type CatalogFiltersOption = { label: string; value: string };

export type CatalogSorting = "default" | "price-asc" | "price-desc";

export type CatalogPageContextData =
  | (SmartphonesProps["catalogData"] &
      SelectedTagState & {
        filters: CatalogFilters;
        filtersOptions: Record<CatalogFiltersAttribute, CatalogFiltersOption[]>;
        filteredAvailableEntries: SmartphonesProps["catalogData"]["availableEntries"];
        filteredComingSoonEntries: SmartphonesProps["catalogData"]["comingSoonEntries"];
        sorting: CatalogSorting;
        filterValueIsChecked: (
          attribute: CatalogFiltersAttribute,
          value: string,
        ) => boolean;
        toggleFilter: (
          attribute: CatalogFiltersAttribute,
          value: string,
        ) => void;
        resetFilters: () => void;
        setSorting: Dispatch<SetStateAction<CatalogSorting>>;
        optionIsPossible: (
          attribute: CatalogFiltersAttribute,
          value: string,
        ) => boolean;
      })
  | (SwapSmartphonesProps["catalogData"] &
      SelectedTagState & {
        filters: CatalogFilters;
        filtersOptions: Record<CatalogFiltersAttribute, CatalogFiltersOption[]>;
        filteredAvailableEntries: SmartphonesProps["catalogData"]["availableEntries"];
        filteredComingSoonEntries: SmartphonesProps["catalogData"]["comingSoonEntries"];
        sorting: CatalogSorting;
        filterValueIsChecked: (
          attribute: CatalogFiltersAttribute,
          value: string,
        ) => boolean;
        toggleFilter: (
          attribute: CatalogFiltersAttribute,
          value: string,
        ) => void;
        resetFilters: () => void;
        setSorting: Dispatch<SetStateAction<CatalogSorting>>;
        optionIsPossible: (
          attribute: CatalogFiltersAttribute,
          value: string,
        ) => boolean;
      })
  | undefined;

export const CatalogPageContext =
  createContext<CatalogPageContextData>(undefined);

type CatalogPageContextProps =
  | Pick<SmartphonesProps, "catalogData">
  | Pick<SwapSmartphonesProps, "catalogData">;

export const CatalogPageContextProvider = ({
  children,
  catalogData,
}: PropsWithChildren<CatalogPageContextProps>) => {
  const router = useRouter();
  const i18n = useI18n();
  const [selectedTag, setSelectedTag] = useState<string>();

  /* Sorting */
  const [sorting, setSorting] = useState<CatalogSorting>("default");

  /* Filters */
  const [filters, setFilters] = useState<CatalogFilters>({
    brand: [],
    stock: [],
    price: [],
    size: [],
    storage: [],
  });

  useEffect(() => {
    const { query } = router;
    const selectedTagParam = query.slug as string | undefined;

    if (
      ![undefined, "legacy", "prochainement", "meilleures-ventes"].includes(
        selectedTagParam,
      )
    ) {
      setSelectedTag(selectedTagParam);
    }

    setFilters({
      brand: query.brand ? `${query.brand}`.split(",") : [],
      stock: query.stock ? `${query.stock}`.split(",") : [],
      price: query.price ? `${query.price}`.split(",") : [],
      size: query.size ? `${query.size}`.split(",") : [],
      storage: query.storage ? `${query.storage}`.split(",") : [],
    });
  }, [router.query]);

  /* Filtered items */
  const [filteredAvailableEntries, setFilteredAvailableEntries] = useState(
    catalogData?.availableEntries,
  );
  const [filteredComingSoonEntries, setFilteredComingSoonEntries] = useState(
    catalogData?.comingSoonEntries,
  );

  const toFilterLabel = (item: string) =>
    item
      .replaceAll(/-/g, " ")
      .replace(/\b\w/g, (l, i) => (i === 0 ? l.toUpperCase() : l.toLowerCase()))
      .replaceAll("Iphone", "iPhone");

  const toFilterLabelFromTag = (item: string) =>
    catalogData?.tags?.find((tag) => tag.slug === item)?.name ||
    toFilterLabel(item);

  const toFilterValue = (item: string) =>
    item.toLowerCase().replaceAll(/ /g, "-");

  const toFilterOption = (item: string) => ({
    label: toFilterLabel(item),
    value: toFilterValue(item),
  });

  const toFilterOptionFromTag = (item: string) => ({
    label: toFilterLabelFromTag(item),
    value: toFilterValue(item),
  });

  const cleanFilterOptions = (options: CatalogFiltersOption[]) => {
    return options.reduce<CatalogFiltersOption[]>((acc, option) => {
      if (!acc.some((item) => item.value === option.value)) {
        acc.push(option);
      }
      return acc;
    }, []);
  };

  const generateFiltersOptions = (
    availableEntries: SmartphonesProps["catalogData"]["availableEntries"],
    comingSoonEntries: SmartphonesProps["catalogData"]["comingSoonEntries"],
  ) => {
    return {
      brand: cleanFilterOptions([
        ...(availableEntries?.map((e) => toFilterOption(e.brand)) || []),
        ...(comingSoonEntries?.map((e) => toFilterOption(e.brand)) || []),
      ]),
      storage: cleanFilterOptions(
        availableEntries?.flatMap(
          (e) => e.availableStorage?.map((s) => toFilterOption(s)) || [],
        ) || [],
      ),
      stock: [
        ...(availableEntries?.some((e) => !e.isOutOfStock)
          ? [
              {
                label: i18n.t("catalog.filters.stock.optInStock"),
                value: "in-stock",
              },
            ]
          : []),
        ...(availableEntries?.some((e) => e.isOutOfStock)
          ? [
              {
                label: i18n.t("catalog.filters.stock.optOutOfStock"),
                value: "out-of-stock",
              },
            ]
          : []),
      ],
      price: [
        ...(availableEntries?.some((e) => e.minimumSubscriptionPrice < 2000)
          ? [
              {
                label: i18n.t("catalog.filters.price.optMin", { value: "20€" }),
                value: "0-2000",
              },
            ]
          : []),
        ...(availableEntries?.some(
          (e) =>
            2000 <= e.minimumSubscriptionPrice &&
            e.minimumSubscriptionPrice <= 3000,
        )
          ? [
              {
                label: i18n.t("catalog.filters.price.opt", {
                  min: "20€",
                  max: "30€",
                }),
                value: "2000-3000",
              },
            ]
          : []),
        ...(availableEntries?.some(
          (e) =>
            3000 <= e.minimumSubscriptionPrice &&
            e.minimumSubscriptionPrice <= 4000,
        )
          ? [
              {
                label: i18n.t("catalog.filters.price.opt", {
                  min: "30€",
                  max: "40€",
                }),
                value: "3000-4000",
              },
            ]
          : []),
        ...(availableEntries?.some(
          (e) =>
            4000 <= e.minimumSubscriptionPrice &&
            e.minimumSubscriptionPrice <= 5000,
        )
          ? [
              {
                label: i18n.t("catalog.filters.price.opt", {
                  min: "40€",
                  max: "50€",
                }),
                value: "4000-5000",
              },
            ]
          : []),
        ...(availableEntries?.some(
          (e) =>
            5000 <= e.minimumSubscriptionPrice &&
            e.minimumSubscriptionPrice <= 6000,
        )
          ? [
              {
                label: i18n.t("catalog.filters.price.opt", {
                  min: "50€",
                  max: "60€",
                }),
                value: "5000-6000",
              },
            ]
          : []),
      ],
      size: cleanFilterOptions([
        ...(availableEntries?.some((e) => e.tagSlugs?.includes("petite-taille"))
          ? [toFilterOptionFromTag("petite-taille")]
          : []),
        ...(comingSoonEntries?.some(
          (e) => e.tagSlugs?.includes("petite-taille"),
        )
          ? [toFilterOptionFromTag("petite-taille")]
          : []),
        ...(availableEntries?.some(
          (e) => e.tagSlugs?.includes("taille-moyenne"),
        )
          ? [toFilterOptionFromTag("taille-moyenne")]
          : []),
        ...(comingSoonEntries?.some(
          (e) => e.tagSlugs?.includes("taille-moyenne"),
        )
          ? [toFilterOptionFromTag("taille-moyenne")]
          : []),
        ...(availableEntries?.some((e) => e.tagSlugs?.includes("grande-taille"))
          ? [toFilterOptionFromTag("grande-taille")]
          : []),
        ...(comingSoonEntries?.some(
          (e) => e.tagSlugs?.includes("grande-taille"),
        )
          ? [toFilterOptionFromTag("grande-taille")]
          : []),
      ]),
    };
  };

  const filtersOptions = generateFiltersOptions(
    catalogData?.availableEntries,
    catalogData?.comingSoonEntries,
  );

  const optionIsPossible = (
    attribute: CatalogFiltersAttribute,
    value: string,
  ) => {
    const futureFilters = {
      ...filters,
      [attribute]: [...filters[attribute], value],
    };
    const futureAvailableEntries = filterAvailableEntries(
      catalogData?.availableEntries,
      futureFilters,
    );
    const futureComingSoonEntries = filterComingSoonEntries(
      catalogData?.comingSoonEntries,
      futureFilters,
    );
    const futureFiltersOptions = generateFiltersOptions(
      futureAvailableEntries,
      futureComingSoonEntries,
    );

    return futureFiltersOptions[attribute].some((o) => o.value === value);
  };

  const filterValueIsChecked = (
    attribute: CatalogFiltersAttribute,
    value: string,
  ) => {
    return filters[attribute].includes(value);
  };

  const toggleFilter = (attribute: CatalogFiltersAttribute, value: string) => {
    let newFilters;
    if (filters[attribute].includes(value)) {
      newFilters = {
        ...filters,
        [attribute]: filters[attribute].filter((v) => v !== value),
      };
    } else {
      newFilters = { ...filters, [attribute]: [...filters[attribute], value] };
    }

    setFilters(newFilters);
    refreshUrlFilters(newFilters);
  };

  const resetFilters = () => {
    const newFilters = {
      brand: [],
      stock: [],
      price: [],
      size: [],
      storage: [],
    };
    setFilters(newFilters);
    refreshUrlFilters(newFilters);
  };

  const refreshUrlFilters = (newFilters: CatalogFilters) => {
    const urlWithoutFilters = router.asPath.split("?")[0];
    const filtersParams = [
      newFilters.brand.length
        ? `brand=${newFilters.brand.sort().join(",")}`
        : undefined,
      newFilters.stock.length
        ? `stock=${newFilters.stock.sort().join(",")}`
        : undefined,
      newFilters.price.length
        ? `price=${newFilters.price.sort().join(",")}`
        : undefined,
      newFilters.size.length
        ? `size=${newFilters.size.sort().join(",")}`
        : undefined,
      newFilters.storage.length
        ? `storage=${newFilters.storage.sort().join(",")}`
        : undefined,
    ]
      .filter(Boolean)
      .join("&");

    router.replace(
      `${urlWithoutFilters}${filtersParams ? "?" : ""}${filtersParams}`,
      undefined,
      { shallow: true },
    );
  };

  const filterAvailableEntries = (
    availableEntries: SmartphonesProps["catalogData"]["availableEntries"],
    targetedFilters: CatalogFilters,
  ) => {
    return (
      availableEntries?.filter((e) => {
        const tag = !selectedTag || e.tagSlugs?.some((t) => t === selectedTag);
        const size =
          targetedFilters.size.length === 0 ||
          e.tagSlugs?.some((t) =>
            targetedFilters.size.includes(toFilterValue(t!)),
          );
        const brand =
          targetedFilters.brand.length === 0 ||
          targetedFilters.brand.includes(toFilterValue(e.brand));
        const stock =
          targetedFilters.stock.length === 0 ||
          (targetedFilters.stock.includes("in-stock") && !e.isOutOfStock) ||
          (targetedFilters.stock.includes("out-of-stock") && e.isOutOfStock);
        const price =
          targetedFilters.price.length === 0 ||
          targetedFilters.price.some(
            (p) =>
              e.minimumSubscriptionPrice >= +p.split("-")[0] &&
              e.minimumSubscriptionPrice <= +p.split("-")[1],
          );
        const storage =
          targetedFilters.storage.length === 0 ||
          e.availableStorage?.some((s) =>
            targetedFilters.storage.includes(toFilterValue(s)),
          );

        return tag && size && brand && stock && price && storage;
      }) || []
    );
  };

  const filterComingSoonEntries = (
    comingSoonEntries: SmartphonesProps["catalogData"]["comingSoonEntries"],
    targetedFilters: CatalogFilters,
  ) => {
    return (
      comingSoonEntries?.filter((e) => {
        const tag = !selectedTag || e.tagSlugs?.some((t) => t === selectedTag);
        const size =
          targetedFilters.size.length === 0 ||
          e.tagSlugs?.some((t) =>
            targetedFilters.size.includes(toFilterValue(t!)),
          );
        const brand =
          targetedFilters.brand.length === 0 ||
          targetedFilters.brand.includes(toFilterValue(e.brand));
        const stock = targetedFilters.stock.length === 0 || false;
        const price = targetedFilters.price.length === 0 || false;
        const storage = targetedFilters.storage.length === 0 || false;

        return tag && size && brand && stock && price && storage;
      }) || []
    );
  };

  useEffect(() => {
    /* Available */
    setFilteredAvailableEntries(
      filterAvailableEntries(catalogData?.availableEntries, filters).sort(
        (a, b) => {
          switch (sorting) {
            case "price-asc":
              return a.minimumSubscriptionPrice - b.minimumSubscriptionPrice;
            case "price-desc":
              return b.minimumSubscriptionPrice - a.minimumSubscriptionPrice;
            default:
              return 0;
          }
        },
      ),
    );

    /* Coming soon */
    setFilteredComingSoonEntries(
      filterComingSoonEntries(catalogData?.comingSoonEntries, filters),
    );
  }, [filters, sorting]);

  return (
    <CatalogPageContext.Provider
      value={{
        ...catalogData,
        selectedTagState: [selectedTag, setSelectedTag],
        filtersOptions,
        filters,
        filteredAvailableEntries,
        filteredComingSoonEntries,
        sorting,
        filterValueIsChecked,
        toggleFilter,
        resetFilters,
        setSorting,
        optionIsPossible,
      }}
    >
      {children}
    </CatalogPageContext.Provider>
  );
};
